/**
 * Rules for one "Development Time & Cost" row of a quotation.
 *
 *  - time + charge/hour given  -> cost = time x hoursPerUnit x charge/hour   (original logic, unchanged)
 *  - time + cost given         -> charge/hour = cost / (time x hoursPerUnit) (back-calculated)
 *  - neither time nor charge   -> cost is a fixed cost entered directly
 *
 * `hoursPerUnit(type)` is supplied by each page so their existing multipliers stay exactly as they were.
 */
export const ONE_TIME = 'One Time';
export const DETAIL_TYPES = ['Days', 'Weeks', 'Months', 'Years', ONE_TIME];

export const CALCULATED = 'calculated';
export const DERIVED = 'derived';
export const FIXED = 'fixed';

export const isBlank = (value) => value === '' || value === null || value === undefined;
const toNumber = (value) => (isBlank(value) ? 0 : Number(value) || 0);
export const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

/** One Time is entered in hours, so it has no unit multiplier. */
const hasApprox = (row) => !isBlank(row.approxDevelopmentTime) && String(row.approxDevelopmentTime).trim() !== '';

const hoursFor = (row, hoursPerUnit) => {
   if (hasApprox(row)) return 0; // approx time replaces type / time / rate
   const time = toNumber(row.developmentTime);
   if (time <= 0) return 0;
   return time * (row.type === ONE_TIME ? 1 : hoursPerUnit(row.type));
};

export const timeUnitLabel = (type) =>
   type === ONE_TIME ? 'hours' : (type || 'weeks').toLowerCase();

export const emptyDetail = (id) => ({
   id,
   description: '',
   type: '',
   developmentTime: '',
   chargePerHour: '',
   approxDevelopmentTime: '',
   total: '',
});

/** Saved rows come back with nulls for the optional fields; inputs need ''. */
export const toFormRows = (details = []) =>
   details.map((d) => ({
      id: d.id,
      description: d.description ?? '',
      type: d.type ?? '',
      developmentTime: d.developmentTime ?? '',
      chargePerHour: d.chargePerHour ?? '',
      approxDevelopmentTime: d.approxDevelopmentTime ?? '',
      total: d.total ?? '',
   }));

/** Effective time / charge per hour / cost of a row, whichever way it was filled in. */
export function resolveDetail(row, hoursPerUnit) {
   if (hasApprox(row)) {
      return { mode: FIXED, developmentTime: null, chargePerHour: null, total: toNumber(row.total) };
   }
   const time = toNumber(row.developmentTime);
   const rate = toNumber(row.chargePerHour);
   const hours = hoursFor(row, hoursPerUnit);

   if (hours > 0 && rate > 0) {
      return { mode: CALCULATED, developmentTime: time, chargePerHour: rate, total: hours * rate };
   }

   const total = toNumber(row.total);
   if (hours > 0) {
      return {
         mode: DERIVED,
         developmentTime: time,
         chargePerHour: total > 0 ? round2(total / hours) : null,
         total,
      };
   }
   return {
      mode: FIXED,
      developmentTime: null,
      chargePerHour: rate > 0 ? rate : null,
      total,
   };
}

export const detailsSubTotal = (rows = [], hoursPerUnit) =>
   rows.reduce((sum, row) => sum + resolveDetail(row, hoursPerUnit).total, 0);

/** Shape sent to the API. '' (not null) because the request is multipart FormData. */
export function toPayloadRow(row, hoursPerUnit) {
   const resolved = resolveDetail(row, hoursPerUnit);
   return {
      description: row.description,
      type: row.type,
      developmentTime: resolved.developmentTime ?? '',
      chargePerHour: resolved.chargePerHour ?? '',
      approxDevelopmentTime: hasApprox(row) ? String(row.approxDevelopmentTime).trim() : '',
      total: resolved.total,
   };
}

/** Cross-field rule for Yup: returns { field, message } or null. */
export function validateDetailCost(row) {
   if (hasApprox(row)) {
      return toNumber(row.total) > 0
         ? null
         : { field: 'total', message: 'Cost is required when Approx Development Time is given' };
   }
   const time = toNumber(row.developmentTime);
   const rate = toNumber(row.chargePerHour);
   if (rate > 0 && time <= 0) {
      return {
         field: 'developmentTime',
         message: 'Development Time is required when Charge Per Hour is given',
      };
   }
   if (!(time > 0 && rate > 0) && toNumber(row.total) <= 0) {
      return {
         field: 'total',
         message:
            time > 0
               ? 'Enter the Cost or the Charge Per Hour'
               : 'Cost is required for a fixed-cost item',
      };
   }
   return null;
}

/**
 * Applies an edit to one field. When the edit takes a row out of "calculated" mode
 * (e.g. the charge/hour is cleared) the last calculated cost is kept as the starting cost.
 */
export function applyDetailChange(setFieldValue, index, row, field, value, hoursPerUnit) {
   const patch = { [field]: value };
   // Typing a cost on a row that has a time overrides the rate: it is back-calculated from the cost
   if (field === 'total' && hoursFor(row, hoursPerUnit) > 0) patch.chargePerHour = '';
   const before = resolveDetail(row, hoursPerUnit);
   if (
      field !== 'total' &&
      before.mode === CALCULATED &&
      resolveDetail({ ...row, ...patch }, hoursPerUnit).mode !== CALCULATED
   ) {
      patch.total = round2(before.total);
   }
   Object.entries(patch).forEach(([key, val]) =>
      setFieldValue(`quotationDetails.${index}.${key}`, val),
   );
}
